import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { urlFor } from '../lib/sanity';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { cart, removeFromCart, updateQuantity, totalPrice, totalItems } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/finalizar-pedido');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-dark-blue/40 backdrop-blur-sm z-[60]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-brand-white shadow-2xl z-[70] flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-brand-black/5 flex items-center justify-between bg-brand-dark-blue text-brand-white">
              <div className="flex items-center gap-3">
                <div className="bg-brand-green p-2 rounded-xl text-brand-dark-blue">
                  <ShoppingBag size={20} />
                </div>
                <h2 className="font-bold text-xl tracking-tighter">Tu Carrito</h2>
                <span className="bg-brand-green text-brand-dark-blue text-[10px] font-black px-2 py-0.5 rounded-full">
                  {totalItems}
                </span>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-brand-white/10 rounded-full transition-colors"
                aria-label="Cerrar carrito"
              >
                <X size={24} />
              </button>
            </div>

            {/* Items List */}
            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="bg-gray-100 p-8 rounded-full text-brand-gray-blue">
                    <ShoppingBag size={48} />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark-blue">Tu carrito está vacío</h3>
                  <p className="text-brand-gray-blue">¿Aún no has elegido tus envases?</p>
                  <button 
                    onClick={onClose}
                    className="btn-primary"
                  >
                    Ver Productos
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.id || item._id} className="flex gap-4 p-4 bg-white rounded-3xl border border-brand-black/5 shadow-sm group">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 flex items-center justify-center p-0">
                      <img 
                        src={item.image?.asset ? urlFor(item.image).width(160).url() : item.image} 
                        alt={item.name} 
                        className="w-full h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-grow space-y-2">
                      <div className="flex justify-between items-start">
                        <h4 className="font-bold text-brand-dark-blue text-sm leading-tight line-clamp-2">
                          {item.name}
                        </h4>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="text-brand-gray-blue hover:text-red-500 transition-colors"
                          aria-label={`Eliminar ${item.name} del carrito`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 bg-gray-50 rounded-full px-3 py-1 border border-brand-black/5">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="text-brand-dark-blue hover:text-brand-green transition-colors"
                            aria-label="Disminuir cantidad"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="font-bold text-sm min-w-[20px] text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="text-brand-dark-blue hover:text-brand-green transition-colors"
                            aria-label="Aumentar cantidad"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <span className="font-bold text-brand-dark-blue">
                          ${((item.price || 0) * item.quantity).toLocaleString('es-CL')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="p-6 border-t border-brand-black/5 bg-gray-50 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-brand-gray-blue font-bold uppercase tracking-widest text-xs">Subtotal Estimado</span>
                  <span className="text-2xl font-black text-brand-dark-blue">
                    ${totalPrice.toLocaleString('es-CL')}
                  </span>
                </div>
                <p className="text-[10px] text-brand-gray-blue text-center italic">
                  * El total final puede variar según costos de envío y volumen de compra.
                </p>
                <div className="space-y-3">
                  <button 
                    onClick={handleCheckout}
                    className="btn-secondary w-full py-4 text-lg shadow-xl shadow-brand-green/20"
                  >
                    <ShoppingBag size={20} />
                    Finalizar Pedido
                  </button>
                  <button 
                    onClick={onClose}
                    className="w-full text-brand-gray-blue font-bold text-sm hover:text-brand-dark-blue transition-colors flex items-center justify-center gap-2"
                  >
                    Seguir Comprando
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
